import UIComponent from "../ui/component.js";
import api from "../api";
import FilesApi from "../api/files";

const CHUNK_SIZE = 50_000_000;
const IDLE = 'idle';
const RECORDING = 'recording';
const PROCESSING = 'processing';

const AUDIO_MIME_TYPES = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
];

export class MeetingTabRecorder extends UIComponent {
    init() {
        this.state = IDLE;
        this.chunks = [];
        this.startedAt = 0;
        this.timerId = null;
        this.recorder = null;
        this.displayStream = null;
        this.micStream = null;
        this.audioContext = null;
        this.recordStream = null;

        this.uploadUrl = this.element.dataset.uploadUrl;
        this.unsupportedLabel = this.element.dataset.unsupported || '';
        this.noAudioLabel = this.element.dataset.noAudio || '';
        this.genericErrorLabel = this.element.dataset.genericError || '';
        this.uploadErrorLabel = this.element.dataset.uploadError || '';

        this.startBtn = this.element.querySelector('[data-tbx-name="startRecordingBtn"]');
        this.stopBtn = this.element.querySelector('[data-tbx-name="stopRecordingBtn"]');
        this.statusEl = this.element.querySelector('[data-tbx-name="recordingStatus"]');
        this.timerEl = this.element.querySelector('[data-tbx-name="recordingTimer"]');
        this.processingEl = this.element.querySelector('[data-tbx-name="recordingProcessing"]');
        this.errorEl = this.element.querySelector('[data-tbx-name="recordingError"]');
        this.micInput = this.element.querySelector('#meeting-tab-microphone');

        this.startBtn?.addEventListener('click', () => this.startRecording());
        this.stopBtn?.addEventListener('click', () => this.stopRecording());

        this.setState(IDLE);
    }

    setState(state) {
        this.state = state;
        this.element.dataset.meetingRecording = state === IDLE ? '0' : '1';

        this.startBtn?.classList.toggle('d-none', state !== IDLE);
        this.stopBtn?.classList.toggle('d-none', state !== RECORDING);
        this.statusEl?.classList.toggle('d-none', state !== RECORDING);
        this.processingEl?.classList.toggle('d-none', state !== PROCESSING);

        if (this.micInput) {
            this.micInput.disabled = state !== IDLE;
        }
    }

    showError(message) {
        if (!this.errorEl) {
            return;
        }

        this.errorEl.textContent = message;
        this.errorEl.classList.remove('d-none');
    }

    clearError() {
        if (!this.errorEl) {
            return;
        }

        this.errorEl.textContent = '';
        this.errorEl.classList.add('d-none');
    }

    async startRecording() {
        if (this.state !== IDLE) {
            return;
        }

        this.clearError();

        if (!navigator.mediaDevices?.getDisplayMedia || typeof MediaRecorder === 'undefined') {
            this.showError(this.unsupportedLabel);
            return;
        }

        const mimeType = this.pickMimeType();
        if (!mimeType) {
            this.showError(this.unsupportedLabel);
            return;
        }

        try {
            if (this.micInput?.checked) {
                this.micStream = await navigator.mediaDevices.getUserMedia({
                    audio: {
                        echoCancellation: true,
                        noiseSuppression: true,
                    },
                });
            }

            this.displayStream = await navigator.mediaDevices.getDisplayMedia({
                video: {
                    displaySurface: 'browser',
                    frameRate: 5,
                },
                audio: {
                    echoCancellation: false,
                    noiseSuppression: false,
                    autoGainControl: false,
                },
                preferCurrentTab: false,
                selfBrowserSurface: 'exclude',
                surfaceSwitching: 'exclude',
                systemAudio: 'include',
                monitorTypeSurfaces: 'exclude',
            });
        } catch (error) {
            this.cleanupStreams();
            if (error?.name === 'NotAllowedError' || error?.name === 'AbortError') {
                return;
            }
            this.showError(this.genericErrorLabel);
            return;
        }

        const audioTracks = [
            ...(this.displayStream?.getAudioTracks() ?? []),
            ...(this.micStream?.getAudioTracks() ?? []),
        ];

        if (audioTracks.length === 0) {
            this.cleanupStreams();
            this.showError(this.noAudioLabel);
            return;
        }

        try {
            this.recordStream = await this.createRecordStream(audioTracks);
            this.chunks = [];
            this.recorder = new MediaRecorder(this.recordStream, {
                mimeType,
                audioBitsPerSecond: 128000,
            });
            this.recorder.addEventListener('dataavailable', (event) => {
                if (event.data?.size) {
                    this.chunks.push(event.data);
                }
            });
            this.recorder.addEventListener('stop', () => this.onRecorderStop(mimeType));

            this.displayStream.getTracks().forEach((track) => {
                track.addEventListener('ended', () => {
                    if (this.state === RECORDING) {
                        this.stopRecording();
                    }
                });
            });

            this.recorder.start(1000);
            this.startedAt = Date.now();
            this.setState(RECORDING);
            this.startTimer();
            window.addEventListener('beforeunload', this.onBeforeUnload);
        } catch (error) {
            this.cleanupStreams();
            this.showError(this.genericErrorLabel);
        }
    }

    async createRecordStream(audioTracks) {
        if (audioTracks.length === 1) {
            return new MediaStream(audioTracks);
        }

        this.audioContext = new AudioContext();
        if (this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
        }

        const destination = this.audioContext.createMediaStreamDestination();
        audioTracks.forEach((track) => {
            const source = this.audioContext.createMediaStreamSource(new MediaStream([track]));
            source.connect(destination);
        });

        return destination.stream;
    }

    stopRecording() {
        if (this.state !== RECORDING) {
            return;
        }

        this.stopTimer();
        this.setState(PROCESSING);

        if (this.recorder && this.recorder.state !== 'inactive') {
            this.recorder.stop();
        } else {
            this.onRecorderStop(this.pickMimeType());
        }
    }

    async onRecorderStop(mimeType) {
        window.removeEventListener('beforeunload', this.onBeforeUnload);
        this.cleanupStreams();

        const blob = new Blob(this.chunks, { type: mimeType.split(';')[0] });
        this.chunks = [];

        if (blob.size === 0) {
            this.setState(IDLE);
            this.showError(this.genericErrorLabel);
            return;
        }

        try {
            const file = this.blobToFile(blob, mimeType);
            const uploaded = await this.uploadFile(file);
            const transcriptionId = Number(uploaded?.transcription_id);

            if (!Number.isFinite(transcriptionId) || transcriptionId <= 0) {
                throw new Error('upload');
            }

            await this.sendToTranscription(transcriptionId);
        } catch (error) {
            this.setState(IDLE);
            this.showError(this.uploadErrorLabel);
        }
    }

    blobToFile(blob, mimeType) {
        const extension = mimeType.includes('ogg') ? 'ogg' : 'webm';
        const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        const type = mimeType.split(';')[0];

        return new File([blob], `meeting-${stamp}.${extension}`, { type });
    }

    async uploadFile(file) {
        const uuid = crypto.randomUUID();
        const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
        let lastResult = null;

        for (let index = 0; index < totalChunks; index += 1) {
            const start = index * CHUNK_SIZE;
            const chunk = file.slice(start, Math.min(start + CHUNK_SIZE, file.size));
            const formData = new FormData();
            formData.append('file', chunk, file.name);
            formData.append('dzuuid', uuid);
            formData.append('dzchunkindex', String(index));
            formData.append('dztotalfilesize', String(file.size));
            formData.append('dzchunksize', String(CHUNK_SIZE));
            formData.append('dztotalchunkcount', String(totalChunks));
            formData.append('dzchunkbyteoffset', String(start));

            lastResult = await api.post(this.uploadUrl, formData);
        }

        return Array.isArray(lastResult) ? lastResult[0] : lastResult;
    }

    async sendToTranscription(transcriptionId) {
        const payload = {
            files: [transcriptionId],
            options: { language: 'auto' },
            return: window.location.href,
        };

        const estimate = await FilesApi.calculate({ files: [transcriptionId] });
        const offers = Object.values(estimate?.offers || {});
        const paidOffer = offers.find((offer) => offer.type === 'payperminute') || offers[0];

        if (paidOffer?.id) {
            await FilesApi.transcribe(payload, { offer: paidOffer.id });
            return;
        }

        await FilesApi.transcribe(payload);
    }

    pickMimeType() {
        if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
            return '';
        }

        return AUDIO_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type)) || '';
    }

    startTimer() {
        this.updateTimer();
        this.timerId = window.setInterval(() => this.updateTimer(), 1000);
    }

    stopTimer() {
        if (this.timerId) {
            window.clearInterval(this.timerId);
            this.timerId = null;
        }
    }

    updateTimer() {
        if (!this.timerEl) {
            return;
        }

        const totalSeconds = Math.max(0, Math.floor((Date.now() - this.startedAt) / 1000));
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        const pad = (value) => String(value).padStart(2, '0');

        this.timerEl.textContent = hours > 0
            ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
            : `${pad(minutes)}:${pad(seconds)}`;
    }

    cleanupStreams() {
        this.displayStream?.getTracks().forEach((track) => track.stop());
        this.micStream?.getTracks().forEach((track) => track.stop());
        this.recordStream?.getTracks().forEach((track) => track.stop());
        this.displayStream = null;
        this.micStream = null;
        this.recordStream = null;

        if (this.audioContext) {
            this.audioContext.close().catch(() => {});
            this.audioContext = null;
        }
    }

    onBeforeUnload = (event) => {
        if (this.state === IDLE) {
            return;
        }

        event.preventDefault();
        event.returnValue = '';
    };
}
